import React, { useCallback, useEffect, useState, useRef, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Image as ImageIcon, Download, Sparkles, RefreshCcw } from 'lucide-react';
const FILTERS = [{
  id: 'none',
  name: 'Normal',
  style: ''
}, {
  id: 'soft-pink',
  name: 'Soft Pink',
  style: 'sepia(0.3) saturate(1.2) hue-rotate(-30deg) brightness(1.1)'
}, {
  id: 'kawaii',
  name: 'Kawaii',
  style: 'contrast(1.1) saturate(1.5) brightness(1.1) hue-rotate(-10deg)'
}, {
  id: 'vintage',
  name: 'Vintage',
  style: 'sepia(0.5) contrast(1.2) brightness(0.9)'
}, {
  id: 'dreamy',
  name: 'Dreamy',
  style: 'blur(0.5px) brightness(1.2) saturate(0.8)'
}, {
  id: 'bw',
  name: 'Black & White',
  style: 'grayscale(1) contrast(1.05) brightness(1.02)'
}, {
  id: 'noir',
  name: 'Noir',
  style: 'grayscale(1) contrast(1.4) brightness(0.9)'
}];
type Frame = {
  id: string;
  name: string;
  bg: string;
  text: string;
  fillColor: string;
  textColor: string;
  accent?: string;
};
const FRAMES: Frame[] = [{
  id: 'polaroid-white',
  name: 'Classic White',
  bg: 'bg-white',
  text: 'text-gray-800',
  fillColor: '#ffffff',
  textColor: '#1f2937'
}, {
  id: 'polaroid-pink',
  name: 'Blush Pink',
  bg: 'bg-pink-100',
  text: 'text-pink-800',
  fillColor: '#ffe4e1',
  textColor: '#900c3f'
}, {
  id: 'polaroid-dots',
  name: 'Polkadot Pink',
  bg: 'bg-polkadot',
  text: 'text-pink-900',
  fillColor: '#fff5f8',
  textColor: '#581845'
}, {
  id: 'polaroid-dots-bw',
  name: 'Polkadot B&W',
  bg: 'bg-polkadot-bw',
  text: 'text-gray-900',
  fillColor: '#ffffff',
  textColor: '#111111'
}, {
  id: 'lavender',
  name: 'Lavender',
  bg: 'bg-lavender',
  text: 'text-purple-800',
  fillColor: '#ede7ff',
  textColor: '#5b21b6'
}, {
  id: 'mint',
  name: 'Mint',
  bg: 'bg-mint',
  text: 'text-emerald-800',
  fillColor: '#d8f5e3',
  textColor: '#065f46'
}, {
  id: 'sunny',
  name: 'Sunny',
  bg: 'bg-sunny',
  text: 'text-amber-800',
  fillColor: '#fff4c2',
  textColor: '#92400e'
}, {
  id: 'stripes',
  name: 'Stripes',
  bg: 'bg-stripes',
  text: 'text-pink-900',
  fillColor: '#fff5f8',
  textColor: '#581845',
  accent: '🎀'
}, {
  id: 'hearts',
  name: 'Hearts',
  bg: 'bg-hearts',
  text: 'text-pink-900',
  fillColor: '#fff0f5',
  textColor: '#900c3f',
  accent: '💖'
}, {
  id: 'checkered',
  name: 'Checkered',
  bg: 'bg-checkered',
  text: 'text-pink-900',
  fillColor: '#fff5f8',
  textColor: '#581845'
}, {
  id: 'sunset',
  name: 'Sunset',
  bg: 'bg-sunset',
  text: 'text-white',
  fillColor: '#ffb6c1',
  textColor: '#ffffff',
  accent: '🌅'
}, {
  id: 'y2k',
  name: 'Y2K Black',
  bg: 'bg-y2k',
  text: 'text-pink-200',
  fillColor: '#1a1a1a',
  textColor: '#ffc0cb',
  accent: '⭐'
}, {
  id: 'coquette',
  name: 'Coquette Ribbon',
  bg: 'bg-coquette',
  text: 'text-pink-900',
  fillColor: '#fff0f5',
  textColor: '#900c3f',
  accent: '🎀'
}];
// Draw a proper heart on a 2D canvas centered at (cx, cy) with given size.
const drawHeart = (ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, color: string) => {
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  const topCurveHeight = size * 0.3;
  ctx.moveTo(cx, cy + topCurveHeight);
  // top left curve
  ctx.bezierCurveTo(cx, cy, cx - size / 2, cy, cx - size / 2, cy + topCurveHeight);
  // bottom left curve
  ctx.bezierCurveTo(cx - size / 2, cy + (size + topCurveHeight) / 2, cx, cy + (size + topCurveHeight) / 2, cx, cy + size);
  // bottom right curve
  ctx.bezierCurveTo(cx, cy + (size + topCurveHeight) / 2, cx + size / 2, cy + (size + topCurveHeight) / 2, cx + size / 2, cy + topCurveHeight);
  // top right curve
  ctx.bezierCurveTo(cx + size / 2, cy, cx, cy, cx, cy + topCurveHeight);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};
// Draw a coquette-style bow centered at (cx, cy)
const drawBow = (ctx: CanvasRenderingContext2D, cx: number, cy: number, size: number, color: string, knotColor: string) => {
  ctx.save();
  ctx.fillStyle = color;
  // left loop
  ctx.save();
  ctx.translate(cx - size * 0.5, cy);
  ctx.rotate(-0.3);
  ctx.beginPath();
  ctx.ellipse(0, 0, size * 0.55, size * 0.35, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // right loop
  ctx.save();
  ctx.translate(cx + size * 0.5, cy);
  ctx.rotate(0.3);
  ctx.beginPath();
  ctx.ellipse(0, 0, size * 0.55, size * 0.35, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
  // tails
  ctx.beginPath();
  ctx.moveTo(cx - size * 0.15, cy + size * 0.1);
  ctx.lineTo(cx - size * 0.35, cy + size * 0.7);
  ctx.lineTo(cx - size * 0.05, cy + size * 0.5);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx + size * 0.15, cy + size * 0.1);
  ctx.lineTo(cx + size * 0.35, cy + size * 0.7);
  ctx.lineTo(cx + size * 0.05, cy + size * 0.5);
  ctx.closePath();
  ctx.fill();
  // knot
  ctx.fillStyle = knotColor;
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.18, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
};
export const Photobooth = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stripRef = useRef<HTMLDivElement>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string>('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [isCountingDown, setIsCountingDown] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [activeFilter, setActiveFilter] = useState(FILTERS[0]);
  const [activeFrame, setActiveFrame] = useState(FRAMES[0]);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const startCamera = async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: {
            ideal: 1280
          },
          height: {
            ideal: 720
          }
        }
      });
      setStream(mediaStream);
      if (videoRef.current) videoRef.current.srcObject = mediaStream;
      setError('');
    } catch (err) {
      setError('Could not access camera. Please make sure you have granted permission.');
      console.error('Camera error:', err);
    }
  };
  const stopCamera = useCallback(() => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
      setIsCameraReady(false);
    }
  }, [stream]);
  useEffect(() => {
    return () => stopCamera();
  }, [stopCamera]);
  const handleVideoPlay = () => setIsCameraReady(true);
  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.filter = activeFilter.style;
        ctx.translate(canvas.width, 0);
        ctx.scale(-1, 1);
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        setPhotos(prev => [...prev, dataUrl]);
      }
    }
  };
  const takeSequence = async () => {
    if (photos.length >= 4) setPhotos([]);
    for (let i = photos.length; i < 4; i++) {
      setIsCountingDown(true);
      for (let c = 3; c > 0; c--) {
        setCountdown(c);
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      capturePhoto();
      setIsCountingDown(false);
      if (i < 3) await new Promise(resolve => setTimeout(resolve, 1000));
    }
  };
  const retake = () => setPhotos([]);
  const downloadStrip = () => {
    if (!stripRef.current || photos.length === 0) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const padding = 20;
    const imgWidth = 400;
    const imgHeight = 300;
    const spacing = 15;
    const footerHeight = 80;
    canvas.width = imgWidth + padding * 2;
    canvas.height = imgHeight * 4 + spacing * 3 + padding * 2 + footerHeight;
    // Background
    if (activeFrame.id === 'sunset') {
      const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      grad.addColorStop(0, '#ffd1dc');
      grad.addColorStop(0.45, '#ffb6c1');
      grad.addColorStop(1, '#ffa07a');
      ctx.fillStyle = grad;
    } else if (activeFrame.id === 'y2k') {
      const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      grad.addColorStop(0, '#1a1a1a');
      grad.addColorStop(1, '#2a1a2a');
      ctx.fillStyle = grad;
    } else {
      ctx.fillStyle = activeFrame.fillColor;
    }
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    // Pattern overlays
    if (activeFrame.id === 'polaroid-dots') {
      ctx.fillStyle = '#ffc0cb';
      for (let x = 12; x < canvas.width; x += 24) {
        for (let y = 12; y < canvas.height; y += 24) {
          ctx.beginPath();
          ctx.arc(x, y, 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    } else if (activeFrame.id === 'polaroid-dots-bw') {
      ctx.fillStyle = '#111111';
      for (let x = 14; x < canvas.width; x += 26) {
        for (let y = 14; y < canvas.height; y += 26) {
          ctx.beginPath();
          ctx.arc(x, y, 2.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    } else if (activeFrame.id === 'stripes') {
      ctx.save();
      ctx.strokeStyle = '#ffc0cb';
      ctx.lineWidth = 6;
      for (let i = -canvas.height; i < canvas.width; i += 14) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i + canvas.height, canvas.height);
        ctx.stroke();
      }
      ctx.restore();
    } else if (activeFrame.id === 'checkered') {
      ctx.fillStyle = '#ffc0cb';
      const s = 16;
      for (let x = 0; x < canvas.width; x += s) {
        for (let y = 0; y < canvas.height; y += s) {
          if ((x / s + y / s) % 2 === 0) ctx.fillRect(x, y, s, s);
        }
      }
    } else if (activeFrame.id === 'hearts') {
      // Tile actual heart shapes
      const tile = 44;
      const heartSize = 18;
      for (let row = 0, ry = 0; ry < canvas.height + tile; ry += tile, row++) {
        const offsetX = row % 2 === 0 ? 0 : tile / 2;
        for (let rx = -tile; rx < canvas.width + tile; rx += tile) {
          drawHeart(ctx, rx + offsetX + tile / 2 - heartSize / 2, ry, heartSize, '#ff69b4');
        }
      }
    } else if (activeFrame.id === 'coquette') {
      // Soft blush gradient base
      const grad = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      grad.addColorStop(0, '#fff0f5');
      grad.addColorStop(1, '#ffe4ec');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      // Tiled small bows
      const tile = 80;
      for (let row = 0, ry = tile / 2; ry < canvas.height; ry += tile, row++) {
        const offsetX = row % 2 === 0 ? 0 : tile / 2;
        for (let rx = tile / 2; rx < canvas.width; rx += tile) {
          drawBow(ctx, rx + offsetX, ry, 26, '#ff8fb1', '#ff4f93');
        }
      }
      // A bigger feature bow at the top center
      drawBow(ctx, canvas.width / 2, 40, 56, '#ff69b4', '#ff1493');
    }
    // Draw photos
    let loadedImages = 0;
    photos.forEach((photoSrc, index) => {
      const img = new Image();
      img.onload = () => {
        const y = padding + index * (imgHeight + spacing);
        ctx.fillStyle = '#000000';
        ctx.fillRect(padding - 2, y - 2, imgWidth + 4, imgHeight + 4);
        ctx.drawImage(img, padding, y, imgWidth, imgHeight);
        loadedImages++;
        if (loadedImages === photos.length) {
          ctx.fillStyle = activeFrame.textColor;
          ctx.font = '30px Pacifico';
          ctx.textAlign = 'center';
          ctx.fillText('SnapMory', canvas.width / 2, canvas.height - 30);
          ctx.font = '14px Poppins';
          const date = new Date().toLocaleDateString();
          ctx.fillText(date, canvas.width / 2, canvas.height - 10);
          const link = document.createElement('a');
          link.download = `snapmory-${Date.now()}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
        }
      };
      img.src = photoSrc;
    });
  };
  return <section id="photobooth" className="py-20 px-4 min-h-screen flex flex-col items-center justify-center relative" data-id="element-93">
      <div className="text-center mb-10" data-id="element-94">
        <h2 className="text-4xl font-bold text-pink-900 mb-4" data-id="element-95">
          Let's Take Some Photos 📸
        </h2>
        <p className="text-pink-700 font-quicksand text-lg" data-id="element-96">
          Smile, pose, and capture the moment!
        </p>
      </div>

      <div className="w-full max-w-6xl grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-97">
        {/* Camera Area */}
        <div className="lg:col-span-8 flex flex-col gap-6" data-id="element-98">
          <div className="glass-panel p-4 rounded-3xl relative overflow-hidden aspect-video flex items-center justify-center bg-pink-50/50" data-id="element-99">
            {!stream ? <div className="text-center" data-id="element-100">
                <div className="w-20 h-20 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-101">
                  <Camera className="w-10 h-10 text-pink-400" data-id="element-102" />
                </div>
                <p className="text-pink-800 font-medium mb-4" data-id="element-103">
                  Camera access required
                </p>
                <button onClick={startCamera} className="bg-gradient-pink text-white px-6 py-3 rounded-full font-semibold hover:scale-105 transition-transform shadow-lg shadow-pink-300/50" data-id="element-104">
                  Enable Camera
                </button>
                {error && <p className="text-red-500 mt-4 text-sm" data-id="element-105">{error}</p>}
              </div> : <>
                <video ref={videoRef} autoPlay playsInline muted onPlay={handleVideoPlay} className="w-full h-full object-cover rounded-2xl -scale-x-100" style={{
              filter: activeFilter.style
            }} data-id="element-106" />

                <AnimatePresence data-id="element-107">
                  {isCountingDown && <motion.div initial={{
                opacity: 0,
                scale: 0.5
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 1.5
              }} className="absolute inset-0 flex items-center justify-center z-20" data-id="element-108">
                      <span className="text-8xl font-bold text-white drop-shadow-[0_0_15px_rgba(255,105,180,0.8)]" data-id="element-109">
                        {countdown}
                      </span>
                    </motion.div>}
                </AnimatePresence>

                {!isCameraReady && <div className="absolute inset-0 flex items-center justify-center bg-pink-50/80 z-10 rounded-2xl" data-id="element-110">
                    <div className="animate-spin-slow" data-id="element-111">
                      <Sparkles className="w-10 h-10 text-pink-400" data-id="element-112" />
                    </div>
                  </div>}
              </>}

            <canvas ref={canvasRef} className="hidden" data-id="element-113" />
          </div>

          {/* Controls */}
          <div className="glass-card p-6 rounded-3xl flex flex-col gap-6" data-id="element-114">
            <div className="w-full" data-id="element-115">
              <label className="block text-sm font-medium text-pink-800 mb-2" data-id="element-116">
                Select Filter
              </label>
              <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-117">
                {FILTERS.map(filter => <button key={filter.id} onClick={() => setActiveFilter(filter)} className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all ${activeFilter.id === filter.id ? 'bg-pink-500 text-white shadow-md' : 'bg-white/50 text-pink-700 hover:bg-white'}`} data-id="element-118">
                    {filter.name}
                  </button>)}
              </div>
            </div>

            <div className="flex justify-center" data-id="element-119">
              {photos.length < 4 ? <button onClick={takeSequence} disabled={!isCameraReady || isCountingDown} className="flex items-center gap-2 bg-gradient-pink text-white px-8 py-4 rounded-full font-bold text-lg hover:scale-105 transition-transform disabled:opacity-50 disabled:hover:scale-100 shadow-lg shadow-pink-300/50 w-full sm:w-auto justify-center" data-id="element-120">
                  <Camera className="w-6 h-6" data-id="element-121" />
                  {photos.length > 0 ? `Take Photo ${photos.length + 1}/4` : 'Start Session'}
                </button> : <button onClick={retake} className="flex items-center gap-2 bg-white text-pink-600 border-2 border-pink-200 px-8 py-4 rounded-full font-bold text-lg hover:bg-pink-50 transition-colors w-full sm:w-auto justify-center" data-id="element-122">
                  <RefreshCcw className="w-6 h-6" data-id="element-123" />
                  Retake
                </button>}
            </div>
          </div>
        </div>

        {/* Preview Area */}
        <div className="lg:col-span-4 flex flex-col items-center" data-id="element-124">
          <div className="w-full max-w-sm mb-4" data-id="element-125">
            <label className="block text-sm font-medium text-pink-800 mb-2 text-center" data-id="element-126">
              Frame Style
            </label>
            <div className="flex gap-2 justify-center flex-wrap" data-id="element-127">
              {FRAMES.map(frame => <button key={frame.id} onClick={() => setActiveFrame(frame)} className={`w-9 h-9 rounded-full border-2 transition-transform ${frame.bg} ${activeFrame.id === frame.id ? 'scale-125 border-pink-500 shadow-md' : 'border-white/80 hover:scale-110'}`} title={frame.name} aria-label={`Select ${frame.name} frame`} data-id="element-128" />)}
            </div>
            <p className="text-center text-xs text-pink-600 mt-2 font-quicksand" data-id="element-129">
              {activeFrame.name}
            </p>
          </div>

          {/* Photostrip Preview */}
          <div ref={stripRef} className={`relative w-full max-w-[280px] p-4 rounded-sm shadow-xl transition-colors duration-300 ${activeFrame.bg}`} data-id="element-130">
            <div className="flex flex-col gap-3" data-id="element-131">
              {[0, 1, 2, 3].map(index => <div key={index} className="aspect-[4/3] bg-gray-900 rounded-sm overflow-hidden relative" data-id="element-132">
                  {photos[index] ? <img src={photos[index]} alt={`Photo ${index + 1}`} className="w-full h-full object-cover" data-id="element-133" /> : <div className="absolute inset-0 flex items-center justify-center text-gray-700" data-id="element-134">
                      <ImageIcon className="w-8 h-8 opacity-50" data-id="element-135" />
                    </div>}
                </div>)}
            </div>

            <div className={`mt-6 text-center ${activeFrame.text}`} data-id="element-136">
              <h3 className="font-pacifico text-2xl mb-1" data-id="element-137">SnapMory</h3>
              <p className="font-quicksand text-xs opacity-80" data-id="element-138">
                {new Date().toLocaleDateString()}
              </p>
            </div>

            {activeFrame.accent && <span className="absolute -top-3 -right-3 text-2xl drop-shadow-md rotate-12 select-none" data-id="element-139">
                {activeFrame.accent}
              </span>}
          </div>

          <AnimatePresence data-id="element-140">
            {photos.length === 4 && <motion.button initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mt-8 flex items-center gap-2 bg-pink-600 text-white px-8 py-3 rounded-full font-bold hover:bg-pink-700 hover:scale-105 transition-all shadow-lg shadow-pink-300/50" onClick={downloadStrip} data-id="element-141">
                <Download className="w-5 h-5" data-id="element-142" />
                Download Strip
              </motion.button>}
          </AnimatePresence>
        </div>
      </div>
    </section>;
};